import { IIdentityProvider, LoginFlow, SSOFlow } from "matrix-js-sdk/src/matrix";
import { type IdTokenClaims } from "oidc-client-ts";
import { OidcAuthService } from "./OidcAuthService";
import { ValidatedServerConfig } from "../../utils/ValidatedServerConfig";
import SdkConfig from "../../SdkConfig";
import { _t } from "../../languageHandler";

/**
 * Oggetto che rappresenta un flusso di autenticazione SSO personalizzato, 
 * che include provider di identità specifici. 
 * Questo oggetto estende il flusso SSO standard (ISSOFlow) 
 * e aggiunge informazioni sui provider di identità supportati, 
 * come Microsoft, Gitlab, Github, Apple, Google, Facebook e Twitter. 
 * Viene utilizzato per gestire l'autenticazione degli utenti tramite questi provider all'interno dell'applicazione.
 */
export interface ICustomSSOFlow extends SSOFlow {
    type: "m.login.sso";
    identity_providers: IIdentityProvider[];
    clientId?: string;
    authorizationUrl?: string;
    tokenUrl?: string;
    scopes?: string[];
    provider: string
}

/**
 * Enum che rappresenta i brand dei provider di identità personalizzati supportati per l'autenticazione SSO.
 */
export enum CustomIdentityProviderBrand {
    Microsoft = "microsoft",
    Gitlab = "gitlab",
    Github = "github",
    Apple = "apple",
    Google = "google",
    Facebook = "facebook",
    Twitter = "twitter",
}

/**
 * Tipo che rappresenta la risposta completa di un login SSO personalizzato,
 * che include tutte le informazioni necessarie per gestire la sessione dell'utente, 
 * come l'URL del homeserver, i token di accesso e refresh ottenuti dall'emittente OIDC, 
 * il client ID utilizzato per l'autenticazione, l'issuer, le claims del token e il provider di identità.
 */
export type CompleteCustomOidcLoginResponse = {
    // url of the homeserver selected during login
    homeserverUrl: string;
    // identity server url as discovered during login
    identityServerUrl?: string;
    // accessToken gained from OIDC token issuer
    accessToken: string;
    // refreshToken gained from OIDC token issuer, when falsy token cannot be refreshed
    refreshToken?: string;
    // idToken gained from OIDC token issuer
    idToken: string;
    // this client's id as registered with the OIDC issuer
    clientId: string;
    // issuer used during authentication
    issuer: string;
    // claims of the given access token; used during token refresh to validate new tokens
    idTokenClaims: IdTokenClaims;
    // provider OIDC
    provider: string;
};

/**
 * Interfaccia che estende IIdentityProvider per rappresentare provider di identità personalizzati
 */
export interface ICustomIdentityProvider extends IIdentityProvider {
    id: string;
    name: string;
    icon?: string;
    brand?: CustomIdentityProviderBrand | string;
}

/**
 * Funzione che verifica se un dato flusso di login è un flusso SSO personalizzato (ICustomSSOFlow).
 */
export function isCustomSSOFlow(flow: LoginFlow | ICustomSSOFlow): flow is ICustomSSOFlow {
    // Controlliamo prima se il tipo di flow è un oggetto e se contiene la proprietà 'type'
    return flow && typeof flow === 'object' && (flow as ICustomSSOFlow).type === "m.login.sso" && Array.isArray((flow as ICustomSSOFlow).identity_providers);
}

/**
 * Tenta di completare il flusso di autorizzazione code flow per ottenere un access token
 * @returns Promise che si risolve con un CompleteCustomOidcLoginResponse quando il login ha successo
 * @throws Quando non è stato possibile ottenere un access token valido
 */
export const completeCustomOidcLogin = async (): Promise<CompleteCustomOidcLoginResponse> => {
    // 1. Crea o prendi l'istanza singleton (una sola volta)
    const oidcService = OidcAuthService.getInstance();

    // 2. Nella pagina di callback (redirectUri) gestisci la risposta
    const user = await oidcService.handleLoginCallback();
    if (!user) throw new Error(_t("error.oauth2_login_process_error"));

    // console.log("Token accesso:", user.access_token);

    const validatedServerConfig: ValidatedServerConfig = SdkConfig.get().validated_server_config!;
    const oidcConfig = oidcService.getOidcConfig()!;
    return {
        homeserverUrl: validatedServerConfig.hsUrl,
        identityServerUrl: validatedServerConfig.isUrl,
        accessToken: user.access_token,
        refreshToken: user.refresh_token,
        idToken: user.id_token!,
        clientId: oidcConfig.clientId,
        issuer: oidcConfig.authority,
        idTokenClaims: user.profile,
        provider: oidcConfig.provider
    };
};